import { createReducer } from '@reduxjs/toolkit'
import {
  addClients,
  addFile,
  addFiles,
  addProjects,
  showError,
  updateFile,
  showAddClientDialog,
  updateSelectedClientId,
  updateSelectedClient,
  showAddFileModal,
  updateSelectedProject,
  updateSelectedOrganization,
  addClientProjects,
  showAddProjectModal,
  updateSelectedFile,
  showErrorMessage,
  showAddOrganizationDialog,
  addUserRecentFiles,
  setSelectedProjectId,
  addProject,
  removeFile,
  removeProject,
  addClient,
  addUserRoles,
  updateUser,
  showAssignProjectDialog,
  showAssignEngineerDialog,
  showUpdatePaymentMethodDialog,
  showOneTimeServicePaymentDialog,
  removeClient,
  setSearchFilter,
  showEditFileModal,
} from '../actions/organization'
import {
  Client,
  Project,
  File,
  Organization,
  UserRecentFile,
  User,
} from '../models/Organization'
import { addClientAsync } from '../services/clientsService'
import { addProjectAsync } from '../services/projectsService'
import { addUserAsync } from '../services/usersService'
import { addUsers } from '../actions/auth'
import { getRandomColor } from '../utils/Common'
import { UserRole } from '../models/AccessControl'

interface OrganizationState {
  clients: Client[]
  projects: Project[]
  files: File[]
  selectedClientProjects: Project[]
  selectedClientId?: number
  selectedOrganization?: Organization
  errorMessage?: string
  showErrorPopup?: boolean
  showAddClientDialog: boolean
  showAddProjectModal: boolean
  showAddOrganizationModal: boolean
  showAssignProjectModal: boolean
  showAssignEngineerModal: boolean
  showAddFileModal: boolean
  showEditFileModal: boolean
  selectedClient?: Client
  selectedProject?: Project
  selectedProjectId?: number
  selectedFile?: File
  searchFilter?: string
  userRecentFiles: UserRecentFile[]
  users?: User[]
  userRoles?: UserRole[]
  userColors?: { id: number; color: string }[]
  showUpdatePaymentMethodModal: boolean
  showOneTimeServicePaymentModal: boolean
}

const initialState: OrganizationState = {
  clients: [],
  projects: [],
  files: [],
  selectedClientProjects: [],
  userRecentFiles: [],
  showAddClientDialog: false,
  showAddProjectModal: false,
  showAddOrganizationModal: false,
  showAddFileModal: false,
  showEditFileModal: false,
  showAssignProjectModal: false,
  showAssignEngineerModal: false,
  showUpdatePaymentMethodModal: false,
  showOneTimeServicePaymentModal: false,
}

const organizationReducer = createReducer(initialState, (builder) => {
  builder.addCase(addClients, (state, action) => {
    state.clients = action.payload
  })

  builder.addCase(addClient, (state, action) => {
    state.clients.push(action.payload)
  })

  builder.addCase(removeClient, (state, action) => {
    const clientIndex = state.clients.findIndex(
      (client) => client.id === action.payload.id,
    )
    if (clientIndex !== undefined && clientIndex !== -1 && state.clients) {
      state.clients.splice(clientIndex, 1)
    }
  })

  builder.addCase(addClientProjects, (state, action) => {
    if (action.payload.clientId) {
      state.selectedClientProjects = action.payload.projects
    }
  })

  builder.addCase(addProjects, (state, action) => {
    state.projects = action.payload
  })

  builder.addCase(addFiles, (state, action) => {
    state.files = action.payload
  })

  builder.addCase(addFile, (state, action) => {
    state.files?.push(action.payload)
  })

  builder.addCase(removeFile, (state, action) => {
    const fileIndex = state.files.findIndex(
      (file) => file.id === action.payload.id,
    )
    if (fileIndex !== undefined && fileIndex !== -1 && state.files) {
      state.files.splice(fileIndex, 1)
    }
  })

  builder.addCase(addProject, (state, action) => {
    state.projects.push(action.payload)
  })

  builder.addCase(removeProject, (state, action) => {
    const projectIndex = state.projects.findIndex(
      (project) => project.id === action.payload.id,
    )
    if (projectIndex !== -1) {
      state.projects.splice(projectIndex, 1)
    }
  })

  builder.addCase(updateFile, (state, action) => {
    const fileIndex = state.files.findIndex(
      (file) => file.id === action.payload.fileId,
    )
    if (fileIndex !== undefined && fileIndex !== -1 && state.files) {
      state.files[fileIndex] = {
        ...state.files?.[fileIndex],
        ...action.payload.newFile,
      }
    }

    const recentFileIndex = state.userRecentFiles.findIndex(
      (recentFile) => recentFile.file_id === (action.payload.fileId as any),
    )
    if (recentFileIndex !== -1) {
      state.userRecentFiles[recentFileIndex] = {
        ...state.userRecentFiles[recentFileIndex],
        file: action.payload.newFile,
      }
    }
  })
  builder.addCase(addClientAsync.rejected, (state, action) => {
    state.showErrorPopup = true
    state.errorMessage = action.error.message
  })
  builder.addCase(addClientAsync.pending, (state, action) => {
    state.showErrorPopup = false
  })
  builder.addCase(showError, (state, action) => {
    state.showErrorPopup = action.payload
  })
  builder.addCase(showAddClientDialog, (state, action) => {
    state.showAddClientDialog = action.payload
  })
  builder.addCase(showAddProjectModal, (state, action) => {
    state.showAddProjectModal = action.payload
  })
  builder.addCase(showAddOrganizationDialog, (state, action) => {
    state.showAddOrganizationModal = action.payload
  })
  builder.addCase(showAssignProjectDialog, (state, action) => {
    state.showAssignProjectModal = action.payload
  })
  builder.addCase(showAssignEngineerDialog, (state, action) => {
    state.showAssignEngineerModal = action.payload
  })
  builder.addCase(updateSelectedClientId, (state, action) => {
    state.selectedClientId = action.payload
  })
  builder.addCase(updateSelectedClient, (state, action) => {
    state.selectedClient = action.payload
  })
  builder.addCase(addProjectAsync.rejected, (state, action) => {
    state.showErrorPopup = true
    state.errorMessage = action.error.message
  })
  builder.addCase(showErrorMessage, (state, action) => {
    state.showErrorPopup = true
    state.errorMessage = action.payload
  })
  builder.addCase(addProjectAsync.pending, (state, action) => {
    state.showErrorPopup = false
  })
  builder.addCase(showAddFileModal, (state, action) => {
    state.showAddFileModal = action.payload
  })
  builder.addCase(showEditFileModal, (state, action) => {
    state.showEditFileModal = action.payload
  })
  builder.addCase(updateSelectedProject, (state, action) => {
    state.selectedProject = action.payload
  })
  builder.addCase(updateSelectedOrganization, (state, action) => {
    state.selectedOrganization = action.payload
  })
  builder.addCase(updateSelectedFile, (state, action) => {
    state.selectedFile = action.payload
  })
  builder.addCase(addUserAsync.rejected, (state, action) => {
    state.showErrorPopup = true
    state.errorMessage = action.error.message
  })
  builder.addCase(addUsers, (state, action) => {
    state.users = action.payload
    state.userColors = []
    for (const user of action.payload) {
      state.userColors.push({ id: user.id, color: getRandomColor() })
    }
  })
  builder.addCase(updateUser, (state, action) => {
    const userIndex = state.users
      ? state.users?.findIndex((user) => user.id === action.payload.userId)
      : -1
    if (state.users && userIndex !== -1) {
      state.users[userIndex] = {
        ...state.users[userIndex],
        ...action.payload.newInfo,
      }
    }
  })
  builder.addCase(addUserRoles, (state, action) => {
    state.userRoles = action.payload
  })
  builder.addCase(addUserRecentFiles, (state, action) => {
    state.userRecentFiles = action.payload
  })
  builder.addCase(setSelectedProjectId, (state, action) => {
    state.selectedProjectId = action.payload
  })
  builder.addCase(showUpdatePaymentMethodDialog, (state, action) => {
    state.showUpdatePaymentMethodModal = action.payload
  })
  builder.addCase(showOneTimeServicePaymentDialog, (state, action) => {
    state.showOneTimeServicePaymentModal = action.payload
  })
  builder.addCase(setSearchFilter, (state, action) => {
    state.searchFilter = action.payload
  })
})

export default organizationReducer
